import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react'
import cx from 'classnames'
import { datadogRum } from '@datadog/browser-rum'
import {
  Alert,
  Primary,
  Text,
  TextField,
  TextLink,
  SvgIcon,
} from '@cinch-labs/design-system'
import { sendDigitalDataEvent } from '@cinch-nx/shared-analytics'
import { Actions, Context, Errors } from '@cinch-nx/data-search'
import styles from './form.module.css'

import { writeEmailToSessionStorage } from '@cinch-labs/customer-data-segment'

type SearchAlertFrequency = '1d' | '7d' | undefined

export interface SearchAlertsFormProps {
  fetchStatus: string
  sendDataToCustomerIo: (email: string, frequency: string) => void
  onFrequencyChanged: Dispatch<SetStateAction<SearchAlertFrequency>>
}

const SearchAlertsForm = ({
  fetchStatus,
  sendDataToCustomerIo,
  onFrequencyChanged,
}: SearchAlertsFormProps) => {
  const requestIsPending = fetchStatus === 'pending'
  const hasServiceError = fetchStatus === 'error'
  const emailInputRef = useRef<HTMLInputElement>(null)
  const radioInputRef = useRef<HTMLInputElement>(null)
  const [showEmailValidationMessage, setShowEmailValidationMessage] =
    useState(false)
  const [showFrequencyValidationMessage, setShowFrequencyValidationMessage] =
    useState(false)

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const sendTrackingEvents = (frequency: string) => {
    datadogRum.addAction(Actions.SEARCH_ALERTS_FORM_SUBMIT, {
      squad: 'search',
      service: 'search-alerts',
    })

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const getAnonymousId = () =>
      window?.analytics && typeof window.analytics.user === 'function'
        ? window.analytics.user().anonymousId()
        : ''

    sendDigitalDataEvent({
      event: {
        name: 'click',
        category: 'search-alerts-form',
        action: 'form submitted',
        label: 'set up search alert',
        value: frequency,
        segmentID: getAnonymousId(),
      },
    })
  }

  const handleAlertFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    const email = formData.get('email')?.toString()
    const frequency = formData
      .get('search-alerts-frequency')
      ?.toString() as SearchAlertFrequency

    const isValidEmail =
      Boolean(email?.length) && emailInputRef.current?.checkValidity()

    const isValidFrequency = frequency !== undefined

    setShowEmailValidationMessage(!isValidEmail)
    setShowFrequencyValidationMessage(!isValidFrequency)

    if (!isValidFrequency && radioInputRef.current) {
      return radioInputRef.current.focus()
    }

    if (!isValidEmail && emailInputRef.current) {
      return emailInputRef.current.focus()
    }

    if (isValidFrequency) {
      sendTrackingEvents(frequency)
    }

    if (!email || !frequency) return

    writeEmailToSessionStorage(email)

    onFrequencyChanged(frequency)

    return sendDataToCustomerIo(email, frequency)
  }

  useEffect(() => {
    if (hasServiceError) {
      datadogRum.addError(
        new Error(Errors.SEARCH_ALERTS_FETCH_ERROR_API_ERROR),
        {
          service: Context.SEARCH_ALERTS_SERVICE,
          squad: Context.SQUAD,
        },
      )
    }
  }, [hasServiceError])

  return (
    <form className={styles.form} onSubmit={handleAlertFormSubmit} noValidate>
      <fieldset
        className={styles.formFieldset}
        aria-describedby={
          showFrequencyValidationMessage
            ? 'frequency-validation-message'
            : undefined
        }
      >
        <Text
          element="legend"
          fontSizeFixed="m"
          fontWeight="bold"
          className={styles.formLegend}
        >
          How often would you like alerts?
        </Text>

        <div className={styles.formField}>
          <input
            className={styles.formRadio}
            id="search-alerts-daily"
            type="radio"
            name="search-alerts-frequency"
            value="1d"
            data-cy="search-alerts-frequency-daily"
            ref={radioInputRef}
          />
          <Text element="label" fontSizeFixed="m" htmlFor="search-alerts-daily">
            Daily
          </Text>
        </div>

        <div className={styles.formField}>
          <input
            className={styles.formRadio}
            id="search-alerts-weekly"
            type="radio"
            name="search-alerts-frequency"
            data-cy="search-alerts-frequency-weekly"
            value="7d"
          />
          <Text
            element="label"
            fontSizeFixed="m"
            htmlFor="search-alerts-weekly"
          >
            Weekly
          </Text>
        </div>
      </fieldset>

      {showFrequencyValidationMessage && (
        <span
          id="frequency-validation-message"
          className={styles.formValidationMessage}
          aria-hidden={true}
        >
          <SvgIcon name="alert" />
          Select how often you would like alerts to continue
        </span>
      )}

      <TextField
        id="search-alerts-email"
        ref={emailInputRef}
        label="Your email address"
        type="email"
        name="email"
        inputMode="email"
        autoComplete="email"
        spellCheck="false"
        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$"
        data-cy="search-alerts-email"
        classNames={{
          container: styles.formInputContainer,
          label: {
            label: styles.formLabel,
          },
          input: cx(styles.formInput, {
            [styles.formInputError]: showEmailValidationMessage,
          }),
        }}
        error={{
          show: false,
          collapse: true,
        }}
        aria-describedby={
          showEmailValidationMessage ? 'email-validation-message' : undefined
        }
      />

      {showEmailValidationMessage && (
        <span
          id="email-validation-message"
          className={styles.formValidationMessage}
          aria-hidden={true}
        >
          <SvgIcon name="alert" />
          Enter a valid email address to continue
        </span>
      )}

      <Text fontSizeFixed="xs" className={styles.formBodyCopy}>
        We&apos;ll only use your email to send you search alerts, unless
        you&apos;re already subscribed to our marketing emails. You can turn off
        search alerts from any alert email.
      </Text>

      <Text fontSizeFixed="xs" className={styles.formBodyCopy}>
        Your data will be processed in accordance with our{' '}
        <TextLink
          textProps={{ fontSizeFixed: 'xs' }}
          target="_blank"
          href="/privacy"
        >
          Privacy Policy
        </TextLink>
        .
      </Text>

      {hasServiceError && (
        <Alert
          id="search-alerts-submission-error"
          className={styles.formErrornMessage}
          type="error"
          title="Cannot set search alerts right now."
          displayPanelVariant
        >
          Come back and try again later. Until then, you can{' '}
          <TextLink
            textProps={{ fontSizeFixed: 's' }}
            href="/used-cars"
            className={styles.heroLink}
          >
            browse our cars.
          </TextLink>
        </Alert>
      )}

      <Primary
        type="submit"
        isLoading={requestIsPending}
        isFullWidth={true}
        data-cy="search-alerts-submit"
        aria-live="assertive"
        aria-describedby={
          hasServiceError ? 'search-alerts-submission-error' : undefined
        }
        classNames={{
          button: cx(styles.formSubmit, {
            [styles.formSubmitPending]: requestIsPending,
          }),
        }}
      >
        <SvgIcon name="mail" size={16} />
        {requestIsPending ? 'Setting search alerts' : 'Set search alerts'}
      </Primary>
    </form>
  )
}

export default SearchAlertsForm
